import { useEffect, useRef } from 'react';

const CELL_SIZE = 8;
const GAP = 8;
const STEP = CELL_SIZE + GAP;
const AMPLITUDE_1 = 6;
const AMPLITUDE_2 = 3;
const WAVELENGTH_1 = 200;
const WAVELENGTH_2 = 130;
const CYCLE_DURATION_1 = 8;
const CYCLE_DURATION_2 = 13;
const VISIBLE_FRACTION = 0.5;
const MAX_OPACITY = 0.45;

// 3D depth
const MIN_SCALE = 0.5;
const MIN_AMPLITUDE_FACTOR = 0.3;
const PERSPECTIVE_COMPRESS = 0.6;

// Rain-drop ripples
const DROP_INTERVAL = 800; // ms between auto drops
const DROP_LIFETIME = 2500;
const DROP_RADIUS = 80;
const DROP_GLOW_STRENGTH = 0.2;
const DROP_COLOR = [50, 90, 140];

// Drifting logo schools
const SCHOOL_COUNT_MIN = 1;
const SCHOOL_COUNT_MAX = 2;
const SCHOOL_SPEED = 0.012; // base cols/ms drift
const SCHOOL_SPEED_VARY = 0.003;
const LOGO_COLOR = [228, 30, 38]; // brand red #e41e26
const LOGO_ROWS = 3;
const LOGO_COLS = 3;

interface Drop {
  x: number;
  y: number;
  born: number;
}

interface FishOffset {
  dc: number; // columns behind leader (<=0)
  dr: number; // rows from leader (even, for stagger)
}

interface School {
  colOffset: number;
  baseRow: number;
  speed: number;
  fish: FishOffset[];
  trailLength: number; // how far the last follower trails behind leader
}

function buildFormation(): FishOffset[] {
  const fish: FishOffset[] = [{ dc: 0, dr: 0 }]; // leader
  const followers = 1 + Math.floor(Math.random() * 2); // 1 or 2
  // first follower: 3–5 cols behind
  const back1 = -(3 + Math.floor(Math.random() * 3));
  const row1 = (Math.floor(Math.random() * 3) - 1) * 2; // -2, 0, or 2
  fish.push({ dc: back1, dr: row1 });
  if (followers === 2) {
    // second follower: further back, 7–10 cols behind leader
    const back2 = -(7 + Math.floor(Math.random() * 4));
    const row2 = (Math.floor(Math.random() * 3) - 1) * 2;
    fish.push({ dc: back2, dr: row2 });
  }
  return fish;
}

function initSchools(totalRows: number, cols: number): School[] {
  const count =
    SCHOOL_COUNT_MIN +
    Math.floor(Math.random() * (SCHOOL_COUNT_MAX - SCHOOL_COUNT_MIN + 1));
  const schools: School[] = [];
  for (let i = 0; i < count; i++) {
    const fish = buildFormation();
    const minDc = Math.min(...fish.map((f) => f.dc));
    const maxDr = Math.max(...fish.map((f) => Math.abs(f.dr))) + LOGO_ROWS;

    const visStart = Math.floor(totalRows * 0.05) + maxDr;
    const visEnd = Math.floor(totalRows * 0.6) - maxDr;
    // squared random biases toward lower rows (bottom of screen)
    const t = Math.random() * Math.random();
    let row =
      visStart + Math.floor(t * Math.max(1, visEnd - visStart));
    if (row % 2 !== 0) row -= 1;

    schools.push({
      colOffset: (cols / count) * i + Math.random() * (cols / count),
      baseRow: Math.max(maxDr, row),
      speed: SCHOOL_SPEED + (Math.random() - 0.5) * 2 * SCHOOL_SPEED_VARY,
      fish,
      trailLength: -minDc + LOGO_COLS,
    });
  }
  return schools;
}

function isSchoolTile(
  col: number,
  rowIndex: number,
  schools: School[]
): boolean {
  for (let s = 0; s < schools.length; s++) {
    const sc = schools[s];
    const leaderCol = Math.floor(sc.colOffset);
    for (let f = 0; f < sc.fish.length; f++) {
      const fish = sc.fish[f];
      const dc = col - (leaderCol + fish.dc);
      const dr = rowIndex - (sc.baseRow + fish.dr);
      if (dc >= 0 && dc < LOGO_COLS && dr >= 0 && dr < LOGO_ROWS) return true;
    }
  }
  return false;
}

export function WaveBackground() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const dropsRef = useRef<Drop[]>([]);
  const timeRef = useRef(0);
  const schoolsRef = useRef<School[] | null>(null);
  const prevTimeRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animId: number;
    const drops = dropsRef.current;
    let lastDrop = 0;
    let w = 0;
    let h = 0;

    function resize() {
      const dpr = window.devicePixelRatio || 1;
      w = canvas!.offsetWidth;
      h = canvas!.offsetHeight;
      canvas!.width = w * dpr;
      canvas!.height = h * dpr;
      ctx!.setTransform(dpr, 0, 0, dpr, 0, 0);
      schoolsRef.current = null;
    }

    resize();
    window.addEventListener('resize', resize);

    // Easter egg: clicks spawn drops
    function handleClick(e: MouseEvent) {
      const rect = canvas!.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      drops.push({ x, y, born: timeRef.current });
    }
    document.addEventListener('mousedown', handleClick);

    function spawnDrop(time: number) {
      const regionTop = h * (1 - VISIBLE_FRACTION);
      drops.push({
        x: Math.random() * w,
        y: regionTop + Math.random() * (h - regionTop),
        born: time,
      });
    }

    function getDropGlow(x: number, y: number, time: number): number {
      let glow = 0;
      for (let i = drops.length - 1; i >= 0; i--) {
        const d = drops[i];
        const age = time - d.born;
        if (age > DROP_LIFETIME) {
          drops.splice(i, 1);
          continue;
        }

        const dx = x - d.x;
        const dy = y - d.y;
        const dist = Math.sqrt(dx * dx + dy * dy);

        const progress = age / DROP_LIFETIME;
        const ringRadius = progress * DROP_RADIUS;
        const ringWidth = DROP_RADIUS * 0.4;

        const distFromRing = Math.abs(dist - ringRadius);
        if (distFromRing > ringWidth) continue;

        const spatial = 1 - distFromRing / ringWidth;
        const temporal = 1 - progress;
        const centerFalloff =
          dist < ringWidth ? (1 - dist / ringWidth) * temporal * 0.5 : 0;

        glow += (spatial * temporal + centerFalloff) * DROP_GLOW_STRENGTH;
      }
      return Math.min(glow, DROP_GLOW_STRENGTH);
    }

    function draw(time: number) {
      timeRef.current = time;
      ctx!.clearRect(0, 0, w, h);

      if (time - lastDrop > DROP_INTERVAL) {
        spawnDrop(time);
        lastDrop = time;
      }

      const regionTop = h * (1 - VISIBLE_FRACTION);
      const cols = Math.ceil(w / STEP) + 2;
      const phase1 = (time / 1000 / CYCLE_DURATION_1) * Math.PI * 2;
      const phase2 = (time / 1000 / CYCLE_DURATION_2) * Math.PI * 2;

      const rowYs: number[] = [];
      let y = h - STEP;
      while (y > regionTop - STEP) {
        rowYs.push(y);
        const nearness = Math.max(
          0,
          Math.min(1, (y - regionTop) / (h - regionTop))
        );
        const spacing =
          STEP * (PERSPECTIVE_COMPRESS + nearness * (1 - PERSPECTIVE_COMPRESS));
        y -= spacing;
      }

      const totalRows = rowYs.length;

      // Update drifting logo schools
      if (!schoolsRef.current) {
        schoolsRef.current = initSchools(totalRows, cols);
        prevTimeRef.current = time;
      }
      const dt = time - prevTimeRef.current;
      prevTimeRef.current = time;
      const schools = schoolsRef.current;
      for (let si = 0; si < schools.length; si++) {
        const sc = schools[si];
        sc.colOffset += sc.speed * dt;
        // wrap when the last trailing fish has exited right
        if (sc.colOffset > cols + 1 + sc.trailLength) {
          sc.colOffset = -(LOGO_COLS + 1);
        }
      }

      for (let i = 0; i < totalRows; i++) {
        const baseY = rowYs[i];
        const nearness =
          totalRows > 1 ? (totalRows - 1 - i) / (totalRows - 1) : 1;

        const scale = MIN_SCALE + nearness * (1 - MIN_SCALE);
        const size = CELL_SIZE * scale;
        const ampFactor =
          MIN_AMPLITUDE_FACTOR + nearness * (1 - MIN_AMPLITUDE_FACTOR);
        const baseAlpha = nearness * nearness * MAX_OPACITY;

        const stagger = i % 2 === 1 ? STEP / 2 : 0;

        for (let col = -1; col < cols; col++) {
          const baseX = col * STEP + stagger;

          const offset =
            (Math.sin(phase1 + (baseX / WAVELENGTH_1) * Math.PI * 2) *
              AMPLITUDE_1 +
              Math.sin(phase2 + (baseX / WAVELENGTH_2) * Math.PI * 2) *
                AMPLITUDE_2) *
            ampFactor;

          const fy = baseY + offset;
          if (fy + size < regionTop || fy > h) continue;

          const cx = baseX + size / 2;
          const cy = fy + size / 2;
          const glow = getDropGlow(cx, cy, time);

          if (isSchoolTile(col, i, schools)) {
            ctx!.fillStyle = `rgba(${LOGO_COLOR[0]}, ${LOGO_COLOR[1]}, ${LOGO_COLOR[2]}, ${baseAlpha})`;
          } else if (glow > 0.01) {
            const r = Math.round(
              30 + (DROP_COLOR[0] - 30) * (glow / DROP_GLOW_STRENGTH)
            );
            const g = Math.round(
              51 + (DROP_COLOR[1] - 51) * (glow / DROP_GLOW_STRENGTH)
            );
            const b = Math.round(
              80 + (DROP_COLOR[2] - 80) * (glow / DROP_GLOW_STRENGTH)
            );
            const alpha = Math.min(baseAlpha + glow, 0.85);
            ctx!.fillStyle = `rgba(${r}, ${g}, ${b}, ${alpha})`;
          } else {
            ctx!.fillStyle = `rgba(30, 51, 80, ${baseAlpha})`;
          }

          ctx!.fillRect(baseX, fy, size, size);
        }
      }

      animId = requestAnimationFrame(draw);
    }

    animId = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
      document.removeEventListener('mousedown', handleClick);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className="absolute inset-0 w-full h-full pointer-events-none"
    />
  );
}
